import React, { useState, useEffect } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";

import GeneralLayout from "components/PageLayouts/GeneralLayout";
import GenericRecordSelection from "components/Records/RecordSelection/GenericRecordSelection";

import AddCommunityPopup from "./Community/AddCommunityPopup";
import CommunityDetail from "./Community/CommunityDetail";
import CommunityBottomDrawer from "./Community/CommunityBottomDrawer";

import { communityAPI } from "api/Community/CommunityAPI";
import { Community as CommunityType } from "api/Community/CommunityInterfaces";

const Community: React.FC = () => {
  const [selectedCommunity, setSelectedCommunity] = useState<CommunityType | null>(null);
  const [selectedNode, setSelectedNode] = useState<any | null>(null);
  const [savingNode, setSavingNode] = useState(false);

  const { id: routeId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  /* -------------------- Node Update -------------------- */
  const handleNodeUpdate = (node: any) => {
    setSavingNode(true);
    setSelectedNode(node);
    setSavingNode(false);
  };

  const handleCommunityCreated = (newCommunity: CommunityType) => {
    setSelectedCommunity(newCommunity);
    navigate(`/community/${newCommunity.id}/`);
  };

  /* -------------------- URL-based selected ID -------------------- */
  const activeId =
    routeId ? Number(routeId) :
    searchParams.get("id") ? Number(searchParams.get("id")) :
    null;

  /* -------------------- Change URL when user selects record -------------------- */
  const handleSelectCommunity = (id: number) => {
    navigate(`/community/${id}/`);
  };

  /* -------------------- Load selected record when URL changes -------------------- */
  useEffect(() => {
    if (!activeId) {
      setSelectedCommunity(null);
      return;
    }

    communityAPI
      .getById(activeId)
      .then((detail) => setSelectedCommunity(detail))
      .catch(() => setSelectedCommunity(null));
  }, [activeId]);

  return (
    <GeneralLayout
      useLeftDrawer
      defaultOpenLeft={true}
      useBottomDrawer
      leftDrawerContent={
        <GenericRecordSelection<CommunityType>
          fetchItems={communityAPI.getAll}
          onSelect={(c) => handleSelectCommunity(c.id)}
          selectedId={activeId ?? null}
          AddPopupComponent={AddCommunityPopup}
          onCreated={handleCommunityCreated}
          colorKey="communitysPrimary"
          idKey="id"
          nameKey={(c) => c.name || `Community ${c.id}`}
          searchPlaceholder="Search communities..."
        />
      }
      bottomDrawerContent={
        activeId ? (
          <CommunityBottomDrawer
            selectedNode={selectedNode}
            onUpdateNode={handleNodeUpdate}
            savingNode={savingNode}
          />
        ) : null
      }
    >
      <CommunityDetail
        community={selectedCommunity}
        selectedNode={selectedNode}
        onNodeSelect={setSelectedNode}
        onUpdateNode={handleNodeUpdate}
        savingNode={savingNode}
        onCommunityCreated={handleCommunityCreated}
      />
    </GeneralLayout>
  );
};

export default Community;
